// Supplier invitation links (/join?invite=...) and the staff outreach log.
// Every call is checked again by the database.

export const INVITE_PATTERN = /^[0-9a-f]{48}$/;
const STORAGE_KEY = 'residentservice360.outreachInvite';

export function inviteFrom(search) {
  const value = new URLSearchParams(search || '').get('invite')?.trim().toLowerCase() || '';
  return INVITE_PATTERN.test(value) ? value : null;
}

export function joinPath(token) { return `/join?invite=${token}`; }

export function signUpPath(token) {
  const params = new URLSearchParams({ workspace: 'supplier', invite: token, next: joinPath(token) });
  return `/sign-up?${params}`;
}

export function signInPath(token) { return `/sign-in?${new URLSearchParams({ next: joinPath(token) })}`; }

export function appLink(token) { return `residentservice360://join?invite=${token}`; }

/** Where a signed-in supplier goes after opening an invitation (the website lists home jobs there). */
export const JOB_DESTINATION = '/supplier/opportunities';

export function rememberInvite(token, storage = globalThis.localStorage) {
  try { storage?.setItem(STORAGE_KEY, token); } catch { /* private mode */ }
}

export function rememberedInvite(storage = globalThis.localStorage) {
  try { const value = storage?.getItem(STORAGE_KEY) || ''; return INVITE_PATTERN.test(value) ? value : null; } catch { return null; }
}

export function forgetInvite(storage = globalThis.localStorage) {
  try { storage?.removeItem(STORAGE_KEY); } catch { /* private mode */ }
}

async function call(client, name, args) {
  if (!client) throw new Error('Resident Service 360 is not connected yet. Please try again later.');
  const { data, error } = await client.rpc(name, args);
  if (error) throw new Error(error.message || 'Something went wrong. Please try again.');
  return data;
}

export const loadInvitePreview = (client, token) => call(client, 'outreach_invite_preview', { p_token: token });
export const claimInvite = (client, token) => call(client, 'claim_outreach_invite', { p_token: token });
export const loadOutreachOverview = (client, limit = 50) => call(client, 'staff_outreach_overview', { p_limit: limit });
export const setOutreachPaused = (client, paused, reason) => call(client, 'staff_set_outreach_paused', { p_paused: paused, p_reason: reason || null });

export function formatDeadline(value) {
  if (!value) return '';
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/London' });
}

export const reasonLabels = {
  sent: 'Invited',
  already_on_app: 'Already on Resident Service 360',
  no_website: 'No website',
  no_email: 'No email on website',
  personal_address: 'Personal address',
  domain_mismatch: 'Email not on their website domain',
  invalid_email: 'Invalid email',
  not_a_contact_address: 'Not a contact address',
  not_limited_company: 'Not shown as a limited company',
  robots_disallowed: 'Website asks bots not to visit',
  site_unreachable: 'Website did not load',
  suppressed: 'Unsubscribed or bounced',
  frequency_cap: 'Invited recently',
  already_invited: 'Already invited for this job',
  job_cap: 'Job limit of 20 reached',
  daily_cap: 'Daily limit reached',
  paused: 'Outreach paused',
  job_not_active: 'Job no longer active',
  not_open: 'Business not open',
  too_far: 'Too far away',
  not_reached: 'Not checked',
  send_failed: 'Email failed',
  send_rejected: 'Email rejected',
};

export const statusLabels = {
  pending: 'Waiting', processing: 'Working', done: 'Done', skipped: 'Skipped', failed: 'Failed', cancelled: 'Cancelled',
  reserved: 'Sending', sent: 'Sent', bounced: 'Bounced', complained: 'Marked as spam', unsubscribed: 'Unsubscribed',
};

export const labelFor = (labels, key) => labels[key] || (key ? String(key).replace(/_/g, ' ') : '');

export function validatePauseReason(paused, reason) {
  const value = String(reason || '').trim();
  if (paused && value.length < 3) return 'Say why you are pausing outreach (at least 3 characters).';
  if (value.length > 500) return 'Keep the reason under 500 characters.';
  return '';
}
